import type { Layout, LayoutNode, PanelInstance, Split, TabGroup } from "./types";

/**
 * DFS recursif sur le sous-arbre. Le visiteur peut retourner `true` pour
 * arreter la traversee.
 */
export function visit(
  node: LayoutNode,
  visitor: (n: LayoutNode) => boolean | void
): boolean {
  if (visitor(node) === true) return true;
  if (node.kind === "split") {
    for (const child of node.children) {
      if (visit(child, visitor)) return true;
    }
  }
  return false;
}

/** Retourne le node avec cet ID, ou null. */
export function findNodeById(layout: Layout, id: string): LayoutNode | null {
  let found: LayoutNode | null = null;
  visit(layout.root, (n) => {
    if (n.id === id) {
      found = n;
      return true;
    }
  });
  return found;
}

/** Retourne le TabGroup contenant cette instance, ou null. */
export function findGroupContaining(
  layout: Layout,
  instanceId: string
): TabGroup | null {
  let found: TabGroup | null = null;
  visit(layout.root, (n) => {
    if (n.kind === "tabs" && n.panels.some((p) => p.id === instanceId)) {
      found = n;
      return true;
    }
  });
  return found;
}

/**
 * Heuristique V0 : retourne le premier TabGroup non-vide rencontre en DFS.
 * Phase 5 introduira un atom `focusedGroupId` pour un actif global.
 */
export function findActiveGroup(layout: Layout): TabGroup | null {
  let found: TabGroup | null = null;
  visit(layout.root, (n) => {
    if (n.kind === "tabs" && n.panels.length > 0) {
      found = n;
      return true;
    }
  });
  return found;
}

/**
 * Map immutable sur l'arbre. Le mapper est appele pour chaque node ; sa
 * valeur de retour remplace le node. Si le mapper retourne la meme reference,
 * le parent garde sa reference (structural sharing).
 *
 * Application bottom-up : les enfants sont mappes avant le parent.
 */
export function mapTree(
  node: LayoutNode,
  mapper: (n: LayoutNode) => LayoutNode
): LayoutNode {
  if (node.kind === "tabs") return mapper(node);

  // Split — recurse first
  const newChildren = node.children.map((c) => mapTree(c, mapper));
  const childrenChanged = newChildren.some((c, i) => c !== node.children[i]);
  const updated: Split = childrenChanged
    ? { ...node, children: newChildren }
    : node;
  return mapper(updated);
}

/** Retourne toutes les PanelInstances du layout, en ordre DFS. */
export function listAllPanelInstances(layout: Layout): PanelInstance[] {
  const out: PanelInstance[] = [];
  visit(layout.root, (n) => {
    if (n.kind === "tabs") out.push(...n.panels);
  });
  return out;
}
